import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from "@/lib/utils";
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { countries } from '@/data/countries';

interface CountrySelectorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  searchPlaceholder?: string;
}

/**
 * Get country flag image URL from country code
 */
const getCountryFlagUrl = (countryCode: string): string => {
  const code = countryCode.toLowerCase();
  return `https://flagcdn.com/w20/${code}.png`;
};

/**
 * Get country name from country code
 */
const getCountryName = (countryCode: string): string => {
  const country = countries.find((c) => c['alpha-2'] === countryCode.toUpperCase());
  return country ? country.name : countryCode;
};

/**
 * Create country options with flag URLs
 */
const createCountryOptions = () => {
  return countries.map((country) => ({
    label: country.name,
    value: country['alpha-2'],
    flagUrl: getCountryFlagUrl(country['alpha-2']),
  }));
};

/**
 * Single-select country selector component with flags
 * Consistent with LanguageSelector style
 */
export const CountrySelector = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  className,
  searchPlaceholder,
}: CountrySelectorProps) => {
  const { t } = useTranslation('common');
  const resolvedPlaceholder = placeholder ?? t('countrySelect.searchPlaceholder');
  const resolvedSearchPlaceholder = searchPlaceholder ?? t('countrySelect.searchPlaceholder');
  const [open, setOpen] = React.useState(false);
  const options = createCountryOptions();
  const selectedCountry = options.find((opt) => opt.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('h-9 w-full justify-between', className)}
        >
          {selectedCountry ? (
            <div className="flex min-w-0 items-center gap-2 truncate">
              <img
                src={selectedCountry.flagUrl}
                alt={selectedCountry.value}
                className="h-3 w-4 shrink-0 object-cover"
              />
              <span className="truncate">{selectedCountry.label}</span>
            </div>
          ) : (
            <span className="text-muted-foreground truncate">{resolvedPlaceholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-0" align="start">
        <Command>
          <CommandInput placeholder={resolvedSearchPlaceholder} />
          <CommandList>
            <CommandEmpty>{t('countrySelect.noCountryFound')}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = value === option.value;
                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                  >
                    <Check className={cn('mr-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')} />
                    <img
                      src={option.flagUrl}
                      alt={option.value}
                      className="mr-2 h-3 w-4 shrink-0 object-cover"
                    />
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

